import { FC } from "preact/compat";
import { NavLink } from "react-router-dom";
import { useAccessCheck } from "~/hooks";
import { FeaturePulse } from "~/organisms";
import { MenuLinkProps } from "./interfaces";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const is2D = <T,>(
    arr: readonly T[] | (readonly T[])[],
): arr is (readonly T[])[] => Array.isArray(arr[0]);

const MenuLink: FC<MenuLinkProps> = ({
    label,
    icon: Icon,
    access = [],
    feature,
    img,
    hidden,
    ...rest
}) => {
    const { isLoading, check } = useAccessCheck();
    const isSatisfied = is2D(access)
        ? access.some((a) => check(a))
        : check(access);

    if (isLoading && hidden) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="flex h-[44px] max-h-[44px] items-center justify-center">
                <Loader width="20" height="20" className="text-sky-500" />
            </div>
        );
    }

    if (!isSatisfied && hidden) {
        return null;
    }

    if (!isSatisfied) {
        return (
            <div
                title={`"${label}" is not available for you at the moment`}
                className="flex h-[44px] max-h-[44px] items-center justify-center py-3"
            >
                <Icon className="text-gray-400" />
            </div>
        );
    }

    const iconContent = img ? (
        <img
            src={img}
            alt={label}
            width="28"
            height="28"
            className="rounded-full border border-white object-cover"
        />
    ) : (
        <Icon className="text-white" />
    );

    if ("onClick" in rest) {
        return (
            <>
                <button
                    type="button"
                    title={rest.tooltip ? undefined : label}
                    aria-label={label}
                    className={cn(
                        "flex h-[44px] max-h-[44px] w-full items-center justify-center outline-white transition duration-100",
                        rest.isActive.value
                            ? "bg-white/35"
                            : "bg-header-mobile",
                    )}
                    onClick={rest.onClick}
                >
                    {iconContent}
                </button>
                {rest.tooltip && (
                    <div
                        className={cn(
                            "invisible absolute bottom-full mb-5 left-full z-[102] translate-x-2 translate-y-[44px] opacity-0 transition duration-100",
                            "w-[260px] rounded border border-gray-300 bg-white p-2 text-sm shadow-lg",
                            "group-hover:visible group-hover:opacity-100",
                        )}
                    >
                        {rest.tooltip}
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <NavLink
                to={rest.path}
                aria-label={label}
                className="relative z-[102] flex h-[44px] max-h-[44px] w-full items-center justify-center bg-header-mobile outline-white transition duration-100"
            >
                {({ isActive }) => (
                    <>
                        {isActive && (
                            <div className="absolute inset-0 bg-white/35" />
                        )}
                        {isActive && (
                            <div className="absolute left-0 top-0 h-full w-1 bg-white" />
                        )}
                        {iconContent}
                    </>
                )}
            </NavLink>
            <NavLink
                to={rest.path}
                className={cn(
                    "absolute left-full top-0 z-[101] flex h-full w-[200px] items-center rounded-r-[25px] bg-header-mobile pl-4 font-bold text-white",
                    "-translate-x-full opacity-0 transition duration-100 group-hover:translate-x-0 group-hover:opacity-100",
                )}
            >
                {label}
            </NavLink>
            {feature && (
                <FeaturePulse
                    feature={feature}
                    color="amber"
                    className="!right-[2px] !top-[2px]"
                />
            )}
        </>
    );
};

export default MenuLink;
